// Contacts tile (companion v1.13 · Pad-A) — the first data tile on the cookie bridge.
// Consult (list grouped by company + search + read-only detail) and add/edit
// (one upsert form, email = the key). Reaches /api/contacts same-origin (the PWA's
// rscan_sid cookie authorises it). Card-scan → contact is a later drop (needs a
// sync OCR endpoint). Vanilla TS to match the rest of the app.
import QRCode from "qrcode";
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml, skeletonFormHtml } from "./cache";
import { makeDropdown } from "./dropdown";
import { renderC360 } from "./c360";
import { attachDictation } from "./dictate";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Contact = {
  id: number | string;
  name: string | null; email: string;
  phone: string | null; mobile: string | null;
  company: string | null; role: string | null; address: string | null;
  linkedin: string | null; website: string | null; comm_style: string | null;
  domain: string | null; saved: boolean;
  has_card?: boolean;
  org_id?: number | null;       // ORG-DATA-OWNERSHIP phase 2: set = org-owned ("Company")
  author_name?: string | null;  // display name of the author (org rows only)
  notes?: string | null; tags?: string[];
};
type Company = { enabled: boolean; orgName: string | null };

let all: Contact[] = [];
let company: Company = { enabled: false, orgName: null };
let loaded = false;
let current: Contact | null = null; // the contact open in the detail view
let ficheDomain = ""; // the company domain open in the Bedrijfsfiche sheet
let ficheLabel = "";

// CLIENT-360-SPEC §9.1 (companion): the company BODY now mounts in TWO places — the standalone
// Bedrijfsfiche sheet and the contact detail's Company tab, which became a real pane instead of a
// jump to the sheet. Both can be alive at once, so per-mount state can no longer be module
// singletons: every mount owns its host, its id prefix and its own dirty-snapshot.
type CompanyMount = {
  host: HTMLElement;
  prefix: string;                              // "cf" (sheet) | "cd2" (contact detail pane)
  domain: string;
  label: string;
  ddRel: { getValue: () => string } | null;
  ddPrimary: { getValue: () => string } | null;
  snap: string;                                // SAVE-CANON C3 dirty check; "" = not seeded yet
};
const mounts = new Map<string, CompanyMount>(); // by prefix

const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// Group by the curated company, else the email domain — but NOT a freemail/ISP
// domain (those go to "Other"; a personal mailbox isn't a company).
const FREEMAIL = new Set([
  "gmail.com", "hotmail.com", "hotmail.be", "outlook.com", "live.com", "msn.com",
  "yahoo.com", "yahoo.fr", "icloud.com", "me.com", "aol.com", "gmx.com", "gmx.net",
  "proton.me", "protonmail.com", "telenet.be", "skynet.be", "scarlet.be",
]);
function companyKey(c: Contact): string {
  if (c.company && c.company.trim()) return c.company.trim();
  const d = (c.domain || "").trim().toLowerCase();
  return d && !FREEMAIL.has(d) ? d : "Other";
}
function initials(c: Contact): string {
  const n = (c.name || "").trim();
  if (n) {
    const p = n.split(/\s+/);
    return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || n[0]!.toUpperCase();
  }
  return (c.email[0] || "?").toUpperCase();
}
function ring(c: Contact): "known" | "partial" | "unknown" {
  if (c.name && c.email && (c.phone || c.mobile || c.company)) return "known";
  if (c.name && c.email) return "partial";
  return "unknown";
}

// ── data ──
// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadContacts(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded;
  if (!loaded) {
    const snap = loadSnap<Contact[]>("contacts");
    if (snap) { all = snap; painted = true; renderList(); }
    else { const list = $("#ctList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const r = await fetch("/api/contacts", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your contacts."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { items?: Contact[]; company?: Company };
    all = j.items || [];
    if (j.company) company = j.company;
    loaded = true;
    saveSnap("contacts", all);
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load contacts. Check your connection and try again.");
  }
}

function renderEmpty(msg: string): void {
  const list = $("#ctList");
  const empty = $("#ctEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
  const count = $("#topCount"); if (count) { count.textContent = ""; count.hidden = true; }
}

// ── list (grouped by company) ──
function renderList(): void {
  const term = ($("#ctSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const items = term
    ? all.filter((c) => `${c.name || ""} ${c.company || ""} ${c.email}`.toLowerCase().includes(term))
    : all;

  // Only drive the top-bar chip while Contacts is the active screen — the Home-boot
  // prefetch renders this list in the background and must not leak a count onto Home.
  const count = $("#topCount");
  if (count && $("#screen-contacts")?.classList.contains("active")) { count.textContent = all.length ? String(all.length) : ""; count.hidden = !all.length; }

  const empty = $("#ctEmpty");
  const list = $("#ctList");
  if (!list) return;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = all.length ? "No contacts match." : "No contacts yet. Tap New to add one."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";

  // group → sort (named companies A→Z, "Other" last; within group by name)
  const groups = new Map<string, Contact[]>();
  for (const c of items) {
    const k = companyKey(c);
    (groups.get(k) || groups.set(k, []).get(k)!).push(c);
  }
  const keys = [...groups.keys()].sort((a, b) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)));

  list.innerHTML = keys
    .map((k) => {
      const members = groups.get(k)!;
      // a fiche needs the company's domain — derive it from the group's contacts (skip "Other"/freemail)
      const gdomain = k === "Other" ? "" : (members.find((c) => c.domain && !FREEMAIL.has(c.domain.toLowerCase()))?.domain || "");
      const rows = members
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
        .map((c) => {
          const sub = [c.role, c.company || c.email].filter(Boolean).join(" · ");
          return (
            `<button class="crow" data-email="${esc(c.email)}">` +
            `<span class="crow-av ${ring(c)}">${esc(initials(c))}</span>` +
            `<span class="crow-info"><span class="crow-name">${esc(c.name || c.email)}${c.org_id != null ? ` <span class="vf-chip-company">${esc(company.orgName || "Company")}</span>` : ""}</span><span class="crow-sub">${esc(sub)}</span></span>` +
            `<span class="crow-chev"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></span>` +
            `</button>`
          );
        })
        .join("");
      const fiche = gdomain
        ? `<button class="cgroup-fiche" data-fiche="${esc(gdomain)}" data-label="${esc(k)}" title="Company info"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/><path d="M9 9v.01M9 13v.01M9 17v.01"/></svg></button>`
        : "";
      return `<div class="cgroup"><div class="cgroup-h"><span>${esc(k)}</span>${fiche}</div>${rows}</div>`;
    })
    .join("");
}

// ── detail (read-only) ──
/** Home-search result tap → straight into the contact card. The caller fires
 *  loadContacts() first; its synchronous snapshot-paint fills `all` before this runs. */
export function openContactByEmail(email: string): void {
  openDetail(email);
}
const companyOkFor = (c: Contact): boolean => {
  const d = (c.domain || "").trim().toLowerCase();
  return !!d && !FREEMAIL.has(d);
};
function openDetail(email: string): void {
  const c = all.find((x) => (x.email || "").toLowerCase() === email.toLowerCase());
  if (!c) return;
  current = c;
  $("#ctQr")?.classList.remove("open");
  const body = $("#ctDetailBody");
  if (body) {
    const field = (k: string, v: string | null, copy = true): string =>
      v
        ? `<div class="cd-row"><div class="cd-kv"><span class="cd-k">${k}</span><span class="cd-v">${esc(v)}</span></div>` +
          (copy ? `<button class="cd-copy" data-copy="${esc(v)}" title="Copy"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg></button>` : "") +
          `</div>`
        : "";
    // This tab HIDES empty fields, so a contact that only ever came from a mail header renders as
    // a lone e-mail row and reads as broken. Before §9.1 nobody saw it — a saved contact opened
    // straight onto the 360 tab — but Contact is the default now, so say plainly that the rest is
    // simply not filled in yet and point at Edit. (Desktop shows empty INPUTS, which never had
    // this problem.)
    const filled = [c.company, c.role, c.phone, c.mobile, c.address, c.linkedin, c.website, c.comm_style, c.notes].filter(Boolean).length;
    const fieldsHtml =
      `<div class="cd-fields">` +
      field("Email", c.email) + field("Company", c.company) + field("Role", c.role) +
      field("Phone", c.phone) + field("Mobile", c.mobile) + field("Address", c.address) +
      field("LinkedIn", c.linkedin) + field("Website", c.website) +
      field("Communication style", c.comm_style, false) +
      field("Notes", c.notes ?? null) +
      field("Tags", c.tags && c.tags.length ? c.tags.map((t) => "#" + t).join("  ") : null, false) +
      `</div>` +
      (filled === 0
        ? `<div class="cd-sparse">Only the e-mail address is saved for this contact — tap <b>Edit</b> to add a name, company, role or phone number.${companyOkFor(c) ? ` The <b>360</b> tab can also look this person up on the web.` : ""}</div>`
        : "") +
      (c.has_card ? `<div class="cd-row" style="justify-content:center;padding-top:4px"><button type="button" id="cdViewOrig" style="background:none;border:0;color:var(--accent,#2dd1a7);font:inherit;text-decoration:underline;cursor:pointer;padding:6px">View the scanned card</button></div>` : "");
    // CLIENT-360-SPEC §9.1 (decided 2026-08-16): the detail is tabbed Contact · Company · 360
    // (Company only when the domain is a real company). Default = ALWAYS Contact — you opened a
    // person, so you get the person. Company is a REAL pane now (the shared company body, mounted
    // under its own id prefix), not a jump to the fiche sheet. Both heavy tabs mount on their
    // first open and are then only hidden, so a toggle never re-fetches.
    const dom = (c.domain || "").trim().toLowerCase();
    const companyOk = !!dom && !FREEMAIL.has(dom);
    body.innerHTML =
      `<div class="cd-hero"><div class="cd-av ${ring(c)}">${esc(initials(c))}</div>` +
      `<div class="cd-name">${esc(c.name || c.email)}</div>` +
      (c.role || c.company ? `<div class="cd-role">${esc([c.role, c.company].filter(Boolean).join(" · "))}</div>` : "") +
      `</div>` +
      `<div class="tk-seg c3m-seg" id="cdSeg">` +
      `<button type="button" data-t="contact">Contact</button>` +
      (companyOk ? `<button type="button" data-t="company">Company</button>` : "") +
      `<button type="button" data-t="c360">360</button>` +
      `</div>` +
      `<div id="cdTabContact">${fieldsHtml}</div>` +
      (companyOk ? `<div id="cdTabCompany" hidden></div>` : "") +
      `<div id="cdTab360" hidden></div>`;
    const vo = $("#cdViewOrig") as HTMLButtonElement | null;
    if (vo) vo.onclick = () => void viewCardOriginal(c.id);

    let c360Rendered = false;
    let companyRendered = false;
    const setTab = (t: "c360" | "contact" | "company"): void => {
      document.querySelectorAll<HTMLElement>("#cdSeg [data-t]").forEach((b) => b.classList.toggle("on", b.getAttribute("data-t") === t));
      const t360 = $("#cdTab360"), tc = $("#cdTabContact"), tco = $("#cdTabCompany");
      if (t360) (t360 as HTMLElement).hidden = t !== "c360";
      if (tc) (tc as HTMLElement).hidden = t !== "contact";
      if (tco) (tco as HTMLElement).hidden = t !== "company";
      if (t === "c360" && !c360Rendered && t360) {
        c360Rendered = true;
        renderC360(t360 as HTMLElement, { id: c.id, name: c.name, email: c.email, company: c.company, domain: c.domain }, { onNavigate: closeDetail });
      }
      if (t === "company" && !companyRendered && tco) {
        companyRendered = true;
        void loadCompanyBody(tco as HTMLElement, "cd2", dom, c.company || dom);
      }
    };
    $("#cdSeg")?.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("[data-t]");
      if (!b) return;
      const t = b.getAttribute("data-t");
      if (t === "c360" || t === "contact" || t === "company") setTab(t);
    });
    setTab("contact");
  }
  $("#ctEdit")?.setAttribute("data-email", c.email);
  $("#ctDetail")?.classList.add("open");
}
// §9.1: the Company pane is a live "Your info" editor, so leaving the detail SAVES it when touched
// (SAVE-CANON C3, same as closing the fiche sheet). The mount is dropped with the screen — its host
// is about to be replaced, and a stale mount would compare against a detached DOM.
function closeDetail(): void {
  void flushMount("cd2").finally(() => mounts.delete("cd2"));
  $("#ctQr")?.classList.remove("open");
  $("#ctDetail")?.classList.remove("open");
}

// Open the stored card image (a compressed grayscale jpeg) via a short-lived presigned URL.
async function viewCardOriginal(id: number | string): Promise<void> {
  const w = window.open("", "_blank"); // open inside the click gesture so Safari doesn't block it
  try {
    const r = await fetch("/api/contacts/file", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id }),
    });
    const j = (await r.json().catch(() => ({}))) as { url?: string };
    if (!r.ok || !j.url) { w?.close(); toast("Couldn't open the card"); return; }
    if (w) w.location.href = j.url; else location.href = j.url;
  } catch { w?.close(); toast("Couldn't open — check your connection"); }
}

// ── vCard (download + QR) ──
const vEsc = (s: string): string => s.replace(/([\\,;])/g, "\\$1").replace(/\n/g, "\\n");
function vcard(c: Contact): string {
  const L = ["BEGIN:VCARD", "VERSION:3.0"];
  if (c.name) L.push(`FN:${vEsc(c.name)}`);
  if (c.company) L.push(`ORG:${vEsc(c.company)}`);
  if (c.role) L.push(`TITLE:${vEsc(c.role)}`);
  if (c.email) L.push(`EMAIL;TYPE=INTERNET:${vEsc(c.email)}`);
  if (c.phone) L.push(`TEL;TYPE=WORK,VOICE:${vEsc(c.phone)}`);
  if (c.mobile) L.push(`TEL;TYPE=CELL:${vEsc(c.mobile)}`);
  if (c.address) L.push(`ADR;TYPE=WORK:;;${vEsc(c.address)};;;;`);
  if (c.website) L.push(`URL:${vEsc(c.website)}`);
  L.push("END:VCARD");
  return L.join("\r\n");
}
const safeName = (c: Contact): string => (c.name || c.email).replace(/[^\w.-]+/g, "_");

async function showQr(): Promise<void> {
  if (!current) return;
  try {
    const url = await QRCode.toDataURL(vcard(current), { width: 512, margin: 1, errorCorrectionLevel: "M" });
    const img = $("#ctQrImg") as HTMLImageElement | null;
    if (img) img.src = url;
    const nm = $("#ctQrName"); if (nm) nm.textContent = current.name || current.email;
    $("#ctQr")?.classList.add("open");
  } catch {
    toast("Couldn't make the QR — try again.");
  }
}

async function downloadVcf(): Promise<void> {
  if (!current) return;
  const text = vcard(current);
  const filename = `${safeName(current)}.vcf`;
  // iOS PWA: prefer the native share sheet (→ Add to Contacts); else download.
  const file = new File([text], filename, { type: "text/vcard" });
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try { await navigator.share({ files: [file] }); } catch { /* user cancelled */ }
    return;
  }
  const blob = new Blob([text], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ── add / edit sheet ──
let cardFile: File | null = null; // the scanned card photo — sent as a grayscale archive on save

function openSheet(edit?: Contact): void {
  cardFile = null; // manual new/edit → no card image
  const title = $("#ctSheetTitle");
  if (title) title.textContent = edit ? "Edit contact" : "New contact";
  const set = (id: string, v: string | null): void => { const el = $(id) as HTMLInputElement | null; if (el) el.value = v || ""; };
  set("#ctfName", edit?.name || ""); set("#ctfEmail", edit?.email || ""); set("#ctfCompany", edit?.company || "");
  set("#ctfRole", edit?.role || ""); set("#ctfPhone", edit?.phone || ""); set("#ctfMobile", edit?.mobile || "");
  // email is the upsert key → lock it when editing
  const emailEl = $("#ctfEmail") as HTMLInputElement | null;
  if (emailEl) emailEl.disabled = !!edit;
  const banner = $("#ctBanner"); if (banner) banner.hidden = true;
  $("#ctScrim")?.classList.add("open");
  $("#ctSheet")?.classList.add("open");
}
function closeSheet(): void { cardFile = null; $("#ctScrim")?.classList.remove("open"); $("#ctSheet")?.classList.remove("open"); }

// ── business-card scan → prefill the New-contact form ──
type CardFields = { name?: string; email?: string; company?: string; role?: string; phone?: string; mobile?: string; website?: string; address?: string };

function openSheetFromCard(f: CardFields): void {
  const title = $("#ctSheetTitle"); if (title) title.textContent = "New contact from card";
  const set = (id: string, v?: string): void => { const el = $(id) as HTMLInputElement | null; if (el) el.value = v || ""; };
  set("#ctfName", f.name); set("#ctfEmail", f.email); set("#ctfCompany", f.company);
  set("#ctfRole", f.role); set("#ctfPhone", f.phone); set("#ctfMobile", f.mobile);
  const emailEl = $("#ctfEmail") as HTMLInputElement | null; if (emailEl) emailEl.disabled = false;
  const banner = $("#ctBanner"); if (banner) banner.hidden = false;
  $("#ctScrim")?.classList.add("open");
  $("#ctSheet")?.classList.add("open");
}

function showScanBusy(on: boolean): void {
  const el = $("#ctScanBusy"); if (el) el.hidden = !on;
}

// Downscale + re-encode to JPEG base64 client-side so the upload stays small (and
// HEIC photos become JPEG, which the OCR accepts).
export function downscaleToJpegB64(file: File, max = 1600, q = 0.85, gray = false): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      if (!ctx) return reject(new Error("no_ctx"));
      ctx.drawImage(img, 0, 0, w, h);
      if (gray) {
        // Luminance grayscale (manual, not ctx.filter — bulletproof on older iOS Safari).
        const d = ctx.getImageData(0, 0, w, h), px = d.data;
        for (let i = 0; i < px.length; i += 4) {
          const l = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) | 0;
          px[i] = px[i + 1] = px[i + 2] = l;
        }
        ctx.putImageData(d, 0, 0);
      }
      const dataUrl = c.toDataURL("image/jpeg", q);
      resolve(dataUrl.slice(dataUrl.indexOf(",") + 1));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("img_load")); };
    img.src = url;
  });
}

async function onCardCaptured(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0];
  input.value = ""; // reset so the next tap re-opens the camera
  if (!file) return;
  showScanBusy(true);
  try {
    const b64 = await downscaleToJpegB64(file);
    const r = await fetch("/api/contacts/scan", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ image_b64: b64 }),
    });
    const j = (await r.json().catch(() => ({}))) as { fields?: CardFields; error?: string };
    showScanBusy(false);
    if (r.ok && j.fields) { cardFile = file; openSheetFromCard(j.fields); return; }
    // Couldn't read it → never a dead-end (#003b): open the form for manual entry, and STILL keep the
    // card photo so the original is archived (viewable later) even though OCR couldn't read it.
    cardFile = file;
    openSheetFromCard({});
    toast("Couldn't read the card — add the details manually.");
  } catch {
    showScanBusy(false);
    toast("Couldn't scan the card — try again.");
  }
}

async function saveContact(e: Event): Promise<void> {
  e.preventDefault();
  const val = (id: string): string => ($(id) as HTMLInputElement | null)?.value.trim() || "";
  const email = val("#ctfEmail");
  if (!email || !email.includes("@")) { toast("A valid email is required."); $("#ctfEmail")?.focus(); return; }
  const payload: Record<string, unknown> = {
    name: val("#ctfName"), email, company: val("#ctfCompany"), role: val("#ctfRole"),
    phone: val("#ctfPhone"), mobile: val("#ctfMobile"),
  };
  const btn = $("#ctSave") as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
  try {
    if (cardFile) {
      const cardB64 = await downscaleToJpegB64(cardFile, 1400, 0.6, true).catch(() => null); // grayscale archive of the scanned card
      if (cardB64) payload.card_b64 = cardB64;
    }
    const r = await fetch("/api/contacts", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json().catch(() => ({}))) as { card_storage_full?: boolean };
    closeSheet();
    // BACKLOG #009: a full storage cap is no longer silent — the contact saved, the card scan did NOT.
    if (j.card_storage_full) toast("Contact saved — but the card scan was NOT stored: storage is full.");
    else toast("Contact saved");
    await loadContacts(true);
    if ($("#ctDetail")?.classList.contains("open")) openDetail(email); // refresh open detail
  } catch {
    toast("Couldn't save — try again.");
  } finally {
    if (btn) btn.disabled = false;
  }
}

// ── company fiche (Bedrijfsfiche) ──
type CompanyFiche = {
  name?: string | null; description?: string | null; url?: string | null; phone?: string | null;
  address?: string | null; lat?: number | null; lng?: number | null; vat?: string | null;
  vat_verified?: boolean; opening_hours?: string[] | null; rating?: number | null;
  rating_count?: number | null; business_status?: string | null; status?: string | null;
  // Third-source firmographics (Exa, findings §3) — what no website carries. Strings as published.
  firmographics?: { employees?: string; founded?: string; parentGroup?: string; industry?: string; headquarters?: string } | null;
};
type Mine = { vat?: string; relationship?: string; primary_contact_id?: string | null };

function closeFiche(): void { $("#cfScrim")?.classList.remove("open"); $("#cfSheet")?.classList.remove("open"); }

// ── the company body as a mountable unit (§9.1) ──
const mEl = (m: CompanyMount, id: string): HTMLElement | null => m.host.querySelector<HTMLElement>(`#${m.prefix}${id}`);
const mVal = (m: CompanyMount, id: string): string =>
  (mEl(m, id) as HTMLInputElement | HTMLTextAreaElement | null)?.value || "";
// SAVE-CANON C3: "Your info" is an EXISTING record → closing SAVES when changed (silent, the
// desktop CompanyBody model — the Save button is gone on both platforms).
const mountNow = (m: CompanyMount): string => JSON.stringify({
  notes: mVal(m, "Notes"), vat: mVal(m, "Vat"),
  rel: m.ddRel?.getValue() || "", primary: m.ddPrimary?.getValue() || "",
});
/** Save a mount's "Your info" if the user touched it, then re-seed its snapshot. */
async function flushMount(prefix: string): Promise<void> {
  const m = mounts.get(prefix);
  if (!m || !m.snap || mountNow(m) === m.snap) return;
  await saveCompanyInfo(m, true);
  m.snap = mountNow(m);
}
async function flushCloseFiche(): Promise<void> {
  if ($("#cfSheet")?.classList.contains("open")) await flushMount("cf");
  closeFiche();
}

// The standalone Bedrijfsfiche sheet: chassis + the Company · 360 tabs around the shared body.
async function openFiche(domain: string, label: string): Promise<void> {
  if (!domain) return;
  ficheDomain = domain;
  ficheLabel = label;
  const body = $("#cfBody");
  if (!body) return;
  $("#cfScrim")?.classList.add("open");
  $("#cfSheet")?.classList.add("open");
  // §9.1: Company leads, 360 is earned — and the 360 mounts only when its tab is first opened.
  body.innerHTML =
    `<div class="tk-seg c3m-seg" id="cfSeg">` +
    `<button type="button" data-t="company" class="on">Company</button>` +
    `<button type="button" data-t="c360">360</button>` +
    `</div>` +
    `<div id="cfTabCompany"></div>` +
    `<div id="cfTab360" hidden></div>`;
  let fiche360Rendered = false;
  $("#cfSeg")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-t]");
    if (!b) return;
    const t = b.getAttribute("data-t");
    document.querySelectorAll<HTMLElement>("#cfSeg [data-t]").forEach((x) => x.classList.toggle("on", x === b));
    const t360 = $("#cfTab360"), tc = $("#cfTabCompany");
    if (t360) (t360 as HTMLElement).hidden = t !== "c360";
    if (tc) (tc as HTMLElement).hidden = t !== "company";
    if (t === "c360" && !fiche360Rendered && t360) {
      fiche360Rendered = true;
      renderC360(t360 as HTMLElement, { id: null, name: null, email: null, company: label || domain, domain: domain.toLowerCase() }, { onNavigate: closeFiche });
    }
  });
  const host = $("#cfTabCompany");
  if (host) await loadCompanyBody(host as HTMLElement, "cf", domain, label);
}

// Trigger the enrichment on demand (the phone asks the Outlook engine, server-to-server),
// with a loading animation, then re-load the mount in place to show the result.
async function enrichMount(m: CompanyMount): Promise<void> {
  if (!m.domain) return;
  m.host.innerHTML = `<div class="cf-fetching"><div class="ms-spin"></div><div class="cf-fetching-t">Fetching company details…</div></div>`;
  try {
    await fetch("/api/company/enrich", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ domain: m.domain }),
    });
  } catch {
    /* the reload below shows whatever landed (or the empty state again) */
  }
  await loadCompanyBody(m.host, m.prefix, m.domain, m.label);
}

// ── The two on-demand web lanes (EXA-CATEGORIES-FINDINGS §4/§6) — desktop parity ────────────────
// Every `skipped` reason gets its own sentence: an empty box is what made the last web lane read
// as broken (§9.7). "No press about an 11-person SME" is an ANSWER, not a failure.
const WEB_SKIP: Record<string, string> = {
  no_company_name: "Get the company info first — a web lookup needs the company's name, not just its domain.",
  no_result: "No filed figures found for this company.",
  no_news: "No recent press about this company — that's normal for a smaller firm.",
  vat_mismatch: "Found accounts, but under a different VAT number — that's another company, so nothing is shown.",
  rate_limited: "Daily enrichment limit reached — try again tomorrow.",
  unavailable: "The web lookup is unavailable right now.",
};
type FinResult = {
  companyName?: string; vat?: string; fiscalYear?: string; revenue?: string; netProfit?: string;
  equity?: string; employees?: string; confirmed?: boolean; confirmed_by?: string | null;
  sources?: { title: string; url: string }[];
};
type NewsResult = { items: { title: string; url: string; date: string | null; snippet: string | null }[] };

async function fetchCompanyWeb(m: CompanyMount, what: "financials" | "news"): Promise<void> {
  const box = mEl(m, "Web");
  if (!box || !m.domain) return;
  box.innerHTML = `<div class="cf-loading">${what === "news" ? "Looking for recent press…" : "Looking up the filed accounts…"}</div>`;
  try {
    const r = await fetch("/api/company/web", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ domain: m.domain, what }),
    });
    const j = (await r.json().catch(() => ({}))) as { result?: FinResult | NewsResult | null; skipped?: string };
    if (!j.result) { box.innerHTML = `<p class="rp-set-hint">${esc(WEB_SKIP[j.skipped || "unavailable"] || WEB_SKIP.unavailable)}</p>`; return; }
    const row = (k: string, v?: string): string => (v ? `<div class="cf-row"><span class="cf-k">${esc(k)}</span><span class="cf-v">${esc(v)}</span></div>` : "");
    if (what === "financials") {
      const f = j.result as FinResult;
      // Never a bare "verified" badge (rule 3): say what confirmed it, or admit that nothing did.
      const note = f.confirmed
        ? `Filed as ${f.companyName} · confirmed via ${f.confirmed_by}`
        : `Filed as ${f.companyName} — no VAT number on file to confirm it's the same company, so check it before you rely on it.`;
      const src = (f.sources || []).slice(0, 2)
        .map((s) => `<a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(webHostOf(s.url))}</a>`).join(", ");
      box.innerHTML = `<div class="cf-rows">${row("Year", f.fiscalYear)}${row("Revenue", f.revenue)}${row("Net result", f.netProfit)}${row("Equity", f.equity)}${row("Staff", f.employees)}</div>` +
        `<p class="rp-set-hint">${esc(note)}${src ? ` · ${src}` : ""}</p>`;
    } else {
      const items = (j.result as NewsResult).items || [];
      box.innerHTML = `<div class="cf-rows">${items.map((n) =>
        `<div class="cf-row"><span class="cf-v"><a href="${esc(n.url)}" target="_blank" rel="noopener">${esc(n.title)}</a>` +
        `<br><small>${esc(webHostOf(n.url))}${n.date ? ` · ${esc(n.date)}` : ""}</small></span></div>`).join("")}</div>`;
    }
  } catch {
    box.innerHTML = `<p class="rp-set-hint">${esc(WEB_SKIP.unavailable)}</p>`;
  }
}
const webHostOf = (u: string): string => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return "web"; } };

/** Fetch + render the company body into `host` under `prefix`. Registers the mount. */
async function loadCompanyBody(host: HTMLElement, prefix: string, domain: string, label: string): Promise<void> {
  host.innerHTML = skeletonFormHtml(4);
  const m: CompanyMount = { host, prefix, domain, label, ddRel: null, ddPrimary: null, snap: "" };
  mounts.set(prefix, m);
  try {
    const r = await fetch(`/api/company?domain=${encodeURIComponent(domain)}`, { credentials: "include" });
    const j = (await r.json()) as { company?: CompanyFiche | null; notes?: string; mine?: Mine };
    renderCompanyBody(m, j.company ?? null, j.notes || "", j.mine || {});
  } catch {
    host.innerHTML = `<div class="cf-loading">Couldn't load the company info. Try again.</div>`;
  }
}

/** The company body — firmographics + "Your info". Chassis-free and TAB-free: the host supplies
 *  both. Every id is prefixed so the sheet's mount and the contact-detail mount never collide. */
function renderCompanyBody(m: CompanyMount, co: CompanyFiche | null, notes: string, mine: Mine): void {
  const { host, prefix: p, domain, label } = m;
  const name = co?.name || label || domain;
  const maps =
    co?.lat != null && co?.lng != null ? `https://maps.google.com/?q=${co.lat},${co.lng}`
    : co?.address ? `https://maps.google.com/?q=${encodeURIComponent(co.address)}`
    : "";
  const row = (k: string, v: string): string => (v ? `<div class="cf-row"><span class="cf-k">${k}</span><span class="cf-v">${v}</span></div>` : "");
  const enriched = !!(co && (co.description || co.address || co.phone || co.url || co.vat));
  const hours = Array.isArray(co?.opening_hours) && co!.opening_hours!.length
    ? row("Hours", co!.opening_hours!.map((h) => esc(String(h))).join("<br>")) : "";

  host.innerHTML =
    `<div class="cf-head"><span class="cf-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/><path d="M9 9v.01M9 13v.01M9 17v.01"/></svg></span>` +
    `<span class="cf-name">${esc(name)}</span>` +
    (co?.business_status && co.business_status !== "OPERATIONAL" ? `<span class="cf-status">${esc(co.business_status.replace(/_/g, " ").toLowerCase())}</span>` : "") +
    `</div>` +
    (co?.description ? `<p class="cf-desc">${esc(co.description)}</p>` : "") +
    (!enriched ? `<div class="cf-empty"><div class="cf-empty-t">No company info yet.</div><button class="cf-fetch" id="${p}Fetch"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.5 9a9 9 0 0 1 14.8-3.4L23 10M1 14l4.7 4.4A9 9 0 0 0 20.5 15"/></svg> Get company info</button><span class="cf-empty-sub">Or just add your own notes below.</span></div>` : "") +
    `<div class="cf-rows">` +
      row("Address", co?.address ? (maps ? `<a href="${esc(maps)}" target="_blank" rel="noopener">${esc(co.address)}</a>` : esc(co.address)) : "") +
      row("Phone", co?.phone ? `<a href="tel:${esc(co.phone)}">${esc(co.phone)}</a>` : "") +
      row("Website", co?.url ? `<a href="${esc(co.url)}" target="_blank" rel="noopener">${esc(co.url.replace(/^https?:\/\//, ""))}</a>` : "") +
      hours +
      (co?.rating ? row("Rating", `${esc(String(co.rating))}★ (${esc(String(co.rating_count || 0))})`) : "") +
      row("Employees", esc(co?.firmographics?.employees || "")) +
      row("Founded", esc(co?.firmographics?.founded || "")) +
      row("Part of", esc(co?.firmographics?.parentGroup || "")) +
      row("Industry", esc(co?.firmographics?.industry || "")) +
      row("Head office", esc(co?.firmographics?.headquarters || "")) +
    `</div>` +
    (maps ? `<a class="cf-maps" href="${esc(maps)}" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Open in Maps</a>` : "") +
    // The two ON-DEMAND web lanes (findings §4/§6), desktop parity. Never automatic: they cost
    // money and are the lanes most likely to answer about a same-named company elsewhere.
    (enriched ? `<div class="cf-weblanes"><button class="cf-webbtn" data-web="financials">Latest financials</button><button class="cf-webbtn" data-web="news">Company news</button></div><div id="${p}Web"></div>` : "") +
    `<div class="cf-edit"><div class="cf-edit-h">Your info</div>` +
      `<div class="cf-field"><label>Relationship</label><div id="${p}RelDD"></div></div>` +
      `<div class="cf-field" id="${p}PrimaryField" hidden><label>Primary contact</label><div id="${p}PrimaryDD"></div></div>` +
      `<div class="cf-field"><label>VAT number</label><input id="${p}Vat" class="cf-input" type="text" placeholder="VAT / BTW number" value="${esc(mine.vat || co?.vat || "")}" />` +
        (co?.vat_verified && (mine.vat || co?.vat) ? `<span class="cf-hint">✓ VIES-verified</span>` : "") + `</div>` +
      `<div class="cf-field"><label>Notes</label><textarea id="${p}Notes" class="cf-input" rows="3" placeholder="Private notes about this company…">${esc(notes)}</textarea></div>` +
    `</div>`; // SAVE-CANON C3: Save button removed — the body auto-saves on close (desktop parity)

  // Custom dropdowns (HARD RULE: never a native <select>).
  const relDD = makeDropdown({
    placeholder: "— none —",
    value: mine.relationship || "",
    options: [
      { value: "", label: "— none —" },
      { value: "customer", label: "Customer" },
      { value: "supplier", label: "Supplier" },
      { value: "prospect", label: "Prospect" },
    ],
  });
  mEl(m, "RelDD")?.appendChild(relDD.el);
  m.ddRel = relDD;

  const orgContacts = all.filter((c) => (c.domain || "").toLowerCase() === domain.toLowerCase());
  if (orgContacts.length) {
    const primDD = makeDropdown({
      placeholder: "— none —",
      value: mine.primary_contact_id || "",
      searchable: orgContacts.length > 5, // long lists → show ~5 + search
      options: [{ value: "", label: "— none —" }, ...orgContacts.map((c) => ({ value: String(c.id), label: c.name || c.email }))],
    });
    mEl(m, "PrimaryDD")?.appendChild(primDD.el);
    mEl(m, "PrimaryField")?.removeAttribute("hidden");
    m.ddPrimary = primDD;
  } else {
    m.ddPrimary = null;
  }

  mEl(m, "Fetch")?.addEventListener("click", () => void enrichMount(m));
  host.querySelectorAll<HTMLElement>("[data-web]").forEach((b) =>
    b.addEventListener("click", () => void fetchCompanyWeb(m, b.getAttribute("data-web") === "news" ? "news" : "financials")));
  m.snap = mountNow(m); // seed AFTER the dropdowns are built — untouched close = no-op
}

async function saveCompanyInfo(m: CompanyMount, silent = false): Promise<void> {
  try {
    const r = await fetch("/api/company", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        domain: m.domain, notes: mVal(m, "Notes"), vat: mVal(m, "Vat"),
        relationship: m.ddRel?.getValue() || "", primary_contact_id: m.ddPrimary?.getValue() || "",
      }),
    });
    if (!r.ok) throw new Error(String(r.status));
    if (!silent) toast("Saved");
  } catch {
    toast("Couldn't save — try again.");
  }
}

export function initContacts(): void {
  const dictBtn = $("#ctfDict");
  if (dictBtn) attachDictation(dictBtn, (txt) => {
    const ta = $("#ctfNotes") as HTMLTextAreaElement | null;
    if (ta) ta.value = (ta.value ? ta.value.trimEnd() + "\n" : "") + txt;
  });
  const search = $("#ctSearch") as HTMLInputElement | null;
  const onSearch = (): void => { renderList(); const cl = $("#ctClear"); if (cl) cl.hidden = !search?.value; };
  search?.addEventListener("input", onSearch);
  $("#ctClear")?.addEventListener("click", () => { if (search) search.value = ""; onSearch(); search?.focus(); });
  $("#ctQrBtn")?.addEventListener("click", () => void showQr());
  $("#ctVcfBtn")?.addEventListener("click", () => void downloadVcf());
  $("#ctQrClose")?.addEventListener("click", () => $("#ctQr")?.classList.remove("open"));
  $("#ctNew")?.addEventListener("click", () => openSheet());
  $("#ctScanCard")?.addEventListener("click", () => ($("#ctCardInput") as HTMLInputElement | null)?.click());
  $("#ctCardInput")?.addEventListener("change", (e) => void onCardCaptured(e.target as HTMLInputElement));
  $("#ctScrim")?.addEventListener("click", closeSheet);
  $("#ctForm")?.addEventListener("submit", (e) => void saveContact(e));
  $("#ctDetailBack")?.addEventListener("click", closeDetail);
  $("#ctEdit")?.addEventListener("click", (e) => {
    const email = (e.currentTarget as HTMLElement).getAttribute("data-email") || "";
    const c = all.find((x) => (x.email || "").toLowerCase() === email.toLowerCase());
    if (c) openSheet(c);
  });
  // list row click (delegated) + copy buttons in detail
  $("#ctList")?.addEventListener("click", (e) => {
    const fiche = (e.target as HTMLElement).closest<HTMLElement>(".cgroup-fiche");
    if (fiche) { void openFiche(fiche.getAttribute("data-fiche") || "", fiche.getAttribute("data-label") || ""); return; }
    const row = (e.target as HTMLElement).closest<HTMLElement>(".crow");
    if (row) openDetail(row.getAttribute("data-email") || "");
  });
  $("#cfScrim")?.addEventListener("click", () => void flushCloseFiche());
  $("#cfClose")?.addEventListener("click", () => void flushCloseFiche());
  $("#ctDetailBody")?.addEventListener("click", (e) => {
    const cp = (e.target as HTMLElement).closest<HTMLElement>(".cd-copy");
    if (cp) {
      const v = cp.getAttribute("data-copy") || "";
      void navigator.clipboard?.writeText(v).then(() => toast("Copied")).catch(() => {});
    }
  });
}
